<div class="ticket-detail-container">

  <!-- Back -->
  <div class="mb-3">
    <button nbButton ghost size="small" (click)="goBack()">
      <nb-icon icon="arrow-back-outline"></nb-icon> Back to Support
    </button>
  </div>

  <!-- Loading state -->
  <div *ngIf="loadingTicket" class="text-center py-4">
    <p>Loading ticket...</p>
  </div>

  <ng-container *ngIf="!loadingTicket && ticket">

    <!-- Section 1: Header -->
    <nb-card class="mb-3">
      <nb-card-body>
        <div class="row align-items-start">
          <div class="col-md-8">
            <p class="ticket-number text-muted mb-1">
              Ticket #{{ ticket.ticketNumber || ticket._id?.slice(-6) }}
            </p>
            <h4 class="mb-2">{{ ticket.subject }}</h4>
            <div class="d-flex flex-wrap gap-2 mb-2">
              <nb-badge
                [text]="(ticket.status || '') | titlecase"
                [status]="getStatusColor(ticket.status)"
                position="top right"
                style="position: static; display: inline-block;">
              </nb-badge>
              <nb-badge
                [text]="(ticket.priority || '') | titlecase"
                [status]="getPriorityColor(ticket.priority)"
                position="top right"
                style="position: static; display: inline-block;">
              </nb-badge>
              <span class="badge bg-secondary text-capitalize">{{ ticket.category }}</span>
            </div>
            <div class="text-muted small">
              <span>Created: {{ ticket.createdAt | date:'dd/MM/yyyy HH:mm' }}</span>
              <span class="ms-3" *ngIf="ticket.createdBy">
                By: <strong>{{ ticket.createdBy?.name || ticket.createdBy }}</strong>
                <span class="badge bg-light text-dark ms-1" *ngIf="ticket.createdBy?.role">
                  {{ ticket.createdBy.role }}
                </span>
              </span>
            </div>
            <div class="mt-2" *ngIf="ticket.description">
              <p class="mb-0">{{ ticket.description }}</p>
            </div>
          </div>

          <!-- Admin action bar -->
          <div class="col-md-4 mt-3 mt-md-0" *ngIf="role === 'admin'">
            <div class="admin-actions">
              <label class="form-label small fw-bold">Update Status</label>
              <div class="d-flex gap-2 mb-3">
                <nb-select [(ngModel)]="selectedStatus" size="small" style="flex: 1;">
                  <nb-option value="open">Open</nb-option>
                  <nb-option value="in_progress">In Progress</nb-option>
                  <nb-option value="resolved">Resolved</nb-option>
                  <nb-option value="closed">Closed</nb-option>
                </nb-select>
                <button nbButton status="primary" size="small" (click)="onStatusChange()" [disabled]="updatingStatus">
                  {{ updatingStatus ? '...' : 'Update' }}
                </button>
              </div>

              <label class="form-label small fw-bold">Assign To (Admin ID)</label>
              <div class="d-flex gap-2">
                <input
                  nbInput
                  fieldSize="small"
                  [(ngModel)]="assignAdminId"
                  placeholder="Admin ID"
                  style="flex: 1;"
                />
                <button nbButton status="basic" size="small" (click)="onAssign()" [disabled]="assigning">
                  {{ assigning ? '...' : 'Assign' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nb-card-body>
    </nb-card>

    <!-- Section 2: Ticket Attachments -->
    <nb-card class="mb-3" *ngIf="ticket.attachments && ticket.attachments.length > 0">
      <nb-card-header>
        <h6 class="mb-0">Attachments</h6>
      </nb-card-header>
      <nb-card-body>
        <ul class="attachment-list">
          <li *ngFor="let att of ticket.attachments">
            <nb-icon icon="file-outline" class="me-1"></nb-icon>
            <a [href]="getAttachmentUrl(att.filename || att)" target="_blank">
              {{ att.originalName || att.filename || att }}
            </a>
            <span class="text-muted small ms-1" *ngIf="att.size">({{ formatFileSize(att.size) }})</span>
          </li>
        </ul>
      </nb-card-body>
    </nb-card>

    <!-- Section 3: Thread -->
    <nb-card class="mb-3">
      <nb-card-header>
        <h6 class="mb-0">Conversation Thread</h6>
      </nb-card-header>
      <nb-card-body>
        <div *ngIf="loadingMessages" class="text-center py-3">
          <p>Loading messages...</p>
        </div>

        <div *ngIf="!loadingMessages && messages.length === 0" class="text-center py-3 text-muted">
          No messages yet. Be the first to reply.
        </div>

        <div *ngFor="let msg of messages" class="message-card mb-3"
             [class.internal-note]="msg.isInternal">
          <div class="message-header d-flex justify-content-between align-items-center mb-1">
            <div>
              <strong>{{ msg.sender?.name || msg.sender || 'Unknown' }}</strong>
              <span class="badge bg-secondary ms-1 small" *ngIf="msg.sender?.role">
                {{ msg.sender.role }}
              </span>
              <span class="internal-label ms-2" *ngIf="msg.isInternal && role === 'admin'">
                [Internal Note]
              </span>
            </div>
            <small class="text-muted">{{ msg.createdAt | date:'dd/MM/yyyy HH:mm' }}</small>
          </div>
          <div class="message-content">{{ msg.content }}</div>

          <div class="mt-2" *ngIf="msg.attachments && msg.attachments.length > 0">
            <small class="text-muted">Attachments:</small>
            <ul class="attachment-list mt-1">
              <li *ngFor="let att of msg.attachments">
                <nb-icon icon="file-outline" class="me-1"></nb-icon>
                <a [href]="getAttachmentUrl(att.filename || att)" target="_blank">
                  {{ att.originalName || att.filename || att }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nb-card-body>
    </nb-card>

    <!-- Reply Form -->
    <nb-card *ngIf="ticket.status !== 'closed'">
      <nb-card-header>
        <h6 class="mb-0">Add Reply</h6>
      </nb-card-header>
      <nb-card-body>
        <form [formGroup]="replyForm" (ngSubmit)="onReplySubmit()">

          <div class="form-group mb-3">
            <textarea
              nbInput
              fullWidth
              formControlName="content"
              rows="4"
              placeholder="Write your reply..."
            ></textarea>
            <div class="text-danger small mt-1"
                 *ngIf="replyForm.get('content')?.invalid && replyForm.get('content')?.touched">
              Reply content is required.
            </div>
          </div>

          <!-- File attachments -->
          <div class="form-group mb-3">
            <input
              type="file"
              #replyFileInput
              multiple
              style="display: none;"
              (change)="onReplyFileSelect($event)"
            />
            <button type="button" nbButton outline size="small" (click)="replyFileInput.click()">
              <nb-icon icon="attach-outline"></nb-icon> Attach Files
            </button>
            <small class="text-muted ms-2">Max 5 files, 10 MB each</small>

            <div *ngIf="replyAttachmentError" class="text-danger small mt-1">{{ replyAttachmentError }}</div>

            <ul class="attachment-list mt-2" *ngIf="replyAttachments.length > 0">
              <li *ngFor="let file of replyAttachments; let i = index" class="attachment-item">
                <nb-icon icon="file-outline" class="me-1"></nb-icon>
                <span>{{ file.name }}</span>
                <span class="text-muted small ms-1">({{ formatFileSize(file.size) }})</span>
                <button type="button" nbButton ghost size="tiny" status="danger" (click)="removeReplyAttachment(i)" class="ms-2">
                  <nb-icon icon="close-outline"></nb-icon>
                </button>
              </li>
            </ul>
          </div>

          <!-- Internal note checkbox — admin only -->
          <div class="form-group mb-3" *ngIf="role === 'admin'">
            <nb-checkbox [(ngModel)]="isInternal" [ngModelOptions]="{standalone: true}">
              Mark as Internal Note (not visible to the reporter)
            </nb-checkbox>
          </div>

          <button
            type="submit"
            nbButton
            status="primary"
            [disabled]="submittingReply"
          >
            <nb-icon icon="paper-plane-outline" *ngIf="!submittingReply"></nb-icon>
            {{ submittingReply ? 'Sending...' : 'Send Reply' }}
          </button>

        </form>
      </nb-card-body>
    </nb-card>

    <nb-card *ngIf="ticket.status === 'closed'">
      <nb-card-body class="text-center text-muted py-3">
        <nb-icon icon="lock-outline"></nb-icon>
        <p class="mb-0 mt-1">This ticket is closed. No further replies can be added.</p>
      </nb-card-body>
    </nb-card>

  </ng-container>

</div>
